/** @format */

import { SHEETS_INFO_TEAMS } from "./constants";
import { mapIcons, bitcoinIcon, bitcoinIconBig } from "./assets/icons";
import { useModal } from "./context/ModalContext";
import { DEV, CATEGORY_BY_LABEL, CATEGORY_IDS } from "./constants";

const SERVICE_TYPES = Object.freeze({
  DIGITAL: 1 << 0, // 1
  LOCAL: 1 << 1, // 2
  PHYSICAL: 1 << 2, // 4
});

export async function fetchSheetData() {
  const markersData = [];

  for (const [team, sheetInfo] of Object.entries(SHEETS_INFO_TEAMS)) {
    try {
      const res = DEV
        ? await fetch(`https://opensheet.elk.sh/${sheetInfo.url}/${sheetInfo.name}`)
        : await fetch("/api/proxy?url=" + encodeURIComponent(`https://opensheet.elk.sh/${sheetInfo.url}/${sheetInfo.name}`));

      const data = await res.json();
      const offset = markersData.length;

      if (!Array.isArray(data)) {
        console.log(`${team} needs to set url script to public in order to be shown`);
        continue;
      }

      data.forEach((obj, index) => {
        const display = Boolean(obj["Display"] ?? obj["Mostrar"]);

        if (!display) return;

        const rawCategory = obj["Categoría / Rubro"] ?? obj["Business Category"];

        const category = normalizeCategory(rawCategory);

        const rawCoordinates = obj["Coordenadas"] ?? obj["Coordinates"] ?? null;

        const coordinates = rawCoordinates ? rawCoordinates.split(",").map(Number).reverse() : null;

        const act = obj["Ultima Vez"] ? Number(obj["Ultima Vez"]) : obj["Timestamp"] ? Number(obj["Timestamp"]) : 1704070861000;

        const keywordsRaw = obj["Palabras claves"] ?? obj["Key Words"] ?? "";

        const keyWords = keywordsRaw ? keywordsRaw.split(",").map((k) => k.trim()) : [];

        const serviceType = getServiceTypeNamesFromBitmap(
          Number(obj["Comercio físico"] ?? obj["Tipo de comercio"] ?? obj["Business Type"]),
        );

        const website = normalizeWebsite(obj["Sitio web "] ?? obj["Website"] ?? "");

        const newMarker = {
          id: offset + index,
          category,
          serviceType,
          coords: coordinates,
          direction: obj["Dirección"] ?? obj["Direction"] ?? "",
          email: obj["Email"] ?? "",
          city: obj["Localidad"] ?? obj["City"] ?? "",
          name: obj["Nombre de negocio"] ?? obj["Business Name"] ?? "",
          keyWords,
          state: obj["Provincia"] ?? obj["State"] ?? "",
          website: website,
          phone: obj["Teléfono negocio"] ?? obj["Business Number"] ?? "",
          logo: obj["logo"] ?? obj["Logo"] ?? "",
          activity: getLastActivity(act),
          timestamp: act,
          discount: obj["Descuento"] ?? obj["Discount"] ?? "",
          offset,
          team,
        };

        markersData[offset + index] = newMarker;
      });
    } catch (error) {
      console.error("Error fetching sheet data:", error);
      return [];
    }
  }

  return markersData;
}

const ICON_BY_CATEGORY = {
  [CATEGORY_IDS.TOURISM]: mapIcons.restaurant,
  [CATEGORY_IDS.CLOTHING]: mapIcons.clothe,
  [CATEGORY_IDS.APPAREL_AND_FOOTWEAR]: mapIcons.clothe,
  [CATEGORY_IDS.DIGITAL_CONSULTING]: mapIcons.lightbulb,
  [CATEGORY_IDS.SERVICES]: mapIcons.gears,
  [CATEGORY_IDS.FOOD_AND_BEVERAGES]: mapIcons.restaurant,
  [CATEGORY_IDS.GASTRONOMY]: mapIcons.restaurant,
  [CATEGORY_IDS.HOME_APPLIANCES]: mapIcons.home,
  [CATEGORY_IDS.HOBBIES]: mapIcons.default,
  [CATEGORY_IDS.HYGIENE_AND_BEAUTY]: mapIcons.perfume,
  [CATEGORY_IDS.TOYS_AND_BOOKS]: mapIcons.robot,
  [CATEGORY_IDS.GARDEN_AND_GIFTS]: mapIcons.flower,
  [CATEGORY_IDS.TRANSPORT_AND_CUSTOMS]: mapIcons.truck,
  [CATEGORY_IDS.OFFICE_SUPPLIES]: mapIcons.chair,
  [CATEGORY_IDS.COMPUTING]: mapIcons.computer,
  [CATEGORY_IDS.OTHER]: mapIcons.default,
};

export function selectIcon(category) {
  return ICON_BY_CATEGORY[category] ?? mapIcons.default;
}

export function formatDistance(distanceMeters) {
  if (distanceMeters < 1000) {
    return `${Math.round(distanceMeters)} metros`;
  } else {
    const distanceKm = distanceMeters / 1000;
    return `${distanceKm.toFixed(1)} km`;
  }
}

export function driveLinkToImgTag(driveUrl) {
  if (driveUrl == undefined || driveUrl == "P" || driveUrl == "No")
    return <div className="h-12 w-12 text-text_color -rotate-45">{bitcoinIcon}</div>;
  let fileId = null;

  let match = driveUrl.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (match) {
    fileId = match[1];
  } else {
    return <div className="h-12 w-12 text-text_color -rotate-45">{bitcoinIcon}</div>;
  }

  const directUrl = `https://drive.google.com/thumbnail?id=${fileId}&sz=w48`;
  return <img src={directUrl} alt="" className="h-12 w-12" referrerPolicy="no-referrer" />;
}

export function driveLinkToImgSrc(driveUrl, size) {
  if (driveUrl == undefined || driveUrl == "P" || driveUrl == "No") return <div className="-rotate-45">{bitcoinIconBig}</div>;
  let fileId = null;

  let match = driveUrl.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (match) {
    fileId = match[1];
  } else {
    return <div className="-rotate-45">{bitcoinIconBig}</div>;
  }

  return <img src={`https://drive.google.com/thumbnail?id=${fileId}`} alt="" className="w-full object-fit" referrerPolicy="no-referrer" />;
}

export function numberToDMS(coord, isLat) {
  const hemisphere = isLat ? (coord >= 0 ? "N" : "S") : coord >= 0 ? "E" : "W";

  const absCoord = Math.abs(coord);
  const degrees = Math.floor(absCoord);
  const minutesFloat = (absCoord - degrees) * 60;
  const minutes = Math.floor(minutesFloat);
  const seconds = (minutesFloat - minutes) * 60;

  return `${degrees}%C2%B0${minutes}'${seconds}%22S${hemisphere}`;
}

export function getLastActivity(dateInput) {
  if (!dateInput) return 3;

  const now = new Date();
  const targetDate = new Date(dateInput * 1);
  const threeMonthsAgo = new Date(now);
  threeMonthsAgo.setMonth(now.getMonth() - 3);

  const sixMonthsAgo = new Date(now);
  sixMonthsAgo.setMonth(now.getMonth() - 6);

  if (targetDate >= threeMonthsAgo) {
    return 1;
  } else if (targetDate >= sixMonthsAgo) {
    return 2;
  } else {
    return 3;
  }
}

export async function updateStore(marker) {
  const fixedPosition = marker.id * 1 - marker.offset + 1;

  const res = await fetch(`/api/update?id=${fixedPosition}&team=${marker.team}`);

  const data = await res.json();

  if (!data.ok) {
    return;
  }
}

export function formatTimestamp(timestamp) {
  if (!timestamp) return [1, 1];
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now - date;

  const msPerDay = 1000 * 60 * 60 * 24;
  const msPerMonth = msPerDay * 30;
  const msPerYear = msPerDay * 365;

  if (diffMs < msPerMonth) {
    const days = Math.floor(diffMs / msPerDay);
    if (days == 0) {
      return [0, days];
    }
    return [1, days];
  } else if (diffMs < msPerYear) {
    const months = Math.floor(diffMs / msPerMonth);
    return [2, months];
  } else {
    const years = Math.floor(diffMs / msPerYear);
    return [3, years];
  }
}

function getServiceTypeNamesFromBitmap(value) {
  if (value < 1 || value > 7) return [];

  return Object.entries(SERVICE_TYPES)
    .filter(([, bit]) => (value & bit) !== 0)
    .map(([name]) => name);
}

function normalizeCategory(value) {
  if (!value) return CATEGORY_IDS.OTROS;

  const key = value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

  return CATEGORY_BY_LABEL[key] ?? CATEGORY_IDS.OTHER;
}

function normalizeWebsite(value) {
  if (!value) return "";
  const v = value.trim().toLowerCase();
  return v === "no" || v === "n/a" ? "" : value.trim();
}
